import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUtility } from '../../../context/UtilityContext';
import {
  receiveCall,
  terminateCall,
} from '../../../reducers/wca/sales-support/sales-support-action';
import { useCall } from '../../../hooks/useCallSocket';
import './CallPanel.scss';

const DEVELOPMENT_MODE = process.env.NODE_ENV === 'development';

const getCallId = (callData = {}) =>
  callData?.msgId ||
  callData?.callId ||
  callData?.id ||
  callData?.message?.id ||
  callData?.message?.session?.id ||
  callData?.message?.session?.callId ||
  callData?.payload?.entry?.[0]?.changes?.[0]?.value?.calls?.[0]?.id ||
  null;

const getCallStatusText = (callState) => {
  switch (callState) {
    case 'incoming':
      return 'Incoming WhatsApp Call';
    case 'initiating':
    case 'calling':
      return 'Calling customer...';
    case 'ringing':
      return 'Calling customer...';
    case 'connecting':
      return 'Connecting WhatsApp Call';
    case 'connected':
      return 'WhatsApp Call Connected';
    case 'busy':
      return 'Customer busy';
    case 'no-answer':
      return 'No answer';
    case 'missed':
      return 'Missed call';
    case 'failed':
      return 'Call failed';
    case 'disconnected':
      return 'Call ended';
    default:
      return 'WhatsApp Call';
  }
};

const getFooterStatusText = (callState) => {
  switch (callState) {
    case 'incoming':
    case 'idle':
      return 'Waiting for response...';
    case 'initiating':
    case 'calling':
      return 'Calling customer...';
    case 'ringing':
      return 'Ringing...';
    case 'connecting':
      return 'Connecting call...';
    case 'connected':
      return 'Call connected';
    case 'failed':
      return 'Call failed';
    case 'no-answer':
      return 'No answer';
    case 'busy':
      return 'Customer busy';
    case 'missed':
      return 'Missed call';
    case 'disconnected':
      return 'Call ended';
    default:
      return 'Waiting for response...';
  }
};

const CallPanel = ({ call, onAccept, onDecline, onClose, onUpdateCall }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [callTimer, setCallTimer] = useState(0);
  const [connectionState, setConnectionState] = useState('new');
  const [isOnHold, setIsOnHold] = useState(false);
  const [position, setPosition] = useState({ left: null, top: 86 });

  const dispatch = useDispatch();
  const { selectedMetadataId } = useUtility();
  const {
    createIncomingAnswer,
    disconnectPeer,
    toggleMute,
    getIsMuted,
    toggleHold: toggleHoldControl,
    getConnectionState,
    getOfferSdp,
    markCallFailed,
  } = useCall();

  const isOpen = call?.isOpen;
  const callerPhone = call?.phone || '';
  const callData = call?.callData;
  const callState = call?.status || 'idle';
  const error = call?.error || '';
  const endedReason = call?.endedReason || '';
  const isIncoming = call?.direction === 'incoming';
  const isOutgoing = call?.direction === 'outgoing';
  const isTerminalState = ['busy', 'no-answer', 'missed', 'failed', 'disconnected'].includes(
    callState,
  );

  const panelRef = useRef(null);
  const headerRef = useRef(null);
  const draggingRef = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const zIndexRef = useRef(11000);

  useEffect(() => {
    if (isOpen) {
      setIsMuted(getIsMuted());
      setIsMinimized(false);
      setCallTimer(0);
      setConnectionState('new');
    } else {
      disconnectPeer();
    }
  }, [disconnectPeer, getIsMuted, isOpen]);

  useEffect(() => {
    if (callState === 'connected') {
      setIsMuted(getIsMuted());
    }
  }, [callState, getIsMuted]);

  useEffect(() => {
    if (callState === 'connected') {
      const interval = setInterval(() => {
        const state = getConnectionState();
        setConnectionState(state || 'unknown');
        setCallTimer((prev) => prev + 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    return undefined;
  }, [callState, getConnectionState]);

  useEffect(() => {
    if (!isOpen) return;

    const shouldClose =
      callState === 'disconnected' ||
      callState === 'failed' ||
      callState === 'busy' ||
      callState === 'no-answer' ||
      callState === 'missed' ||
      connectionState === 'disconnected' ||
      connectionState === 'failed' ||
      connectionState === 'closed';

    if (shouldClose) {
      const closeTimer = window.setTimeout(() => {
        onClose?.();
      }, 1200);

      return () => window.clearTimeout(closeTimer);
    }
  }, [callState, connectionState, isOpen, onClose]);

  useEffect(() => {
    if (position.left === null && typeof window !== 'undefined') {
      const width = Math.min(380, Math.max(320, Math.floor(window.innerWidth * 0.28)));
      setPosition({ left: window.innerWidth - width - 28, top: position.top || 86 });
    }

    const handleResize = () => {
      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 8);
      setPosition((prev) => ({
        left: Math.min(prev.left, maxLeft),
        top: Math.min(prev.top, maxTop),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position.top, position.left]);

  const logCallDebug = (label, payload) => {
    if (!DEVELOPMENT_MODE) return;
    console.log(`[WCA Call Panel] ${label}`, payload);
  };

  const handleAccept = async () => {
    const offerSdp = getOfferSdp(callData);

    if (!offerSdp) {
      markCallFailed('Invalid call data');
      return;
    }

    try {
      onUpdateCall({ status: 'connecting', error: '' });
      const answerSdp = await createIncomingAnswer(offerSdp);

      await dispatch(
        receiveCall({
          metadataid: selectedMetadataId,
          phone: callerPhone,
          data: {
              callId: getCallId(callData),
              msgId: getCallId(callData),
              sdpAnswer: answerSdp,
          },
        }),
      ).unwrap();

      onUpdateCall((prev) => ({
        status: 'connected',
        error: '',
        callData: { ...(prev.callData || {}), sdpAnswer: answerSdp },
      }));
      onAccept(answerSdp);
    } catch (err) {
      console.error('Error accepting call:', err);
      markCallFailed(`Failed to accept call: ${err?.message || err}`);
      onUpdateCall({ status: 'incoming' });
    }
  };

  const handleDecline = async () => {
    disconnectPeer();
    setIsMinimized(true);
    onDecline();

    if (callData) {
      try {
        await dispatch(
          terminateCall({
            metadataid: selectedMetadataId,
            phone: callerPhone,
            data: {
              callId: getCallId(callData),
              msgId: getCallId(callData),
              action: 'REJECT',
            },
          }),
        ).unwrap();
      } catch (err) {
        console.error('Error rejecting call:', err);
      }
    }
  };

  const handleDisconnect = async () => {
    if (callState === 'busy' || callState === 'no-answer' || callState === 'missed') {
      onClose();
      return;
    }

    const callId = getCallId(callData);
    const msgId = callData?.msgId || callId;
    const isPreConnectOutgoing = isOutgoing && ['initiating', 'calling', 'ringing', 'connecting'].includes(callState);
    const terminateAction = isPreConnectOutgoing ? 'CANCEL' : 'TERMINATE';

    logCallDebug('active call before cancel/end', {
      callState,
      callId,
      msgId,
      callerPhone,
      direction: call?.direction,
      callData,
    });

    if (!callId && !msgId) {
      console.warn('[WCA Call Panel] terminate requested without callId/msgId', callData);
    }

    disconnectPeer();
    setCallTimer(0);
    setIsMinimized(true);
    onUpdateCall({
      status: 'disconnected',
      error: '',
      endedReason: isPreConnectOutgoing ? 'cancelled' : 'terminated',
      endedAt: Date.now(),
    });

    if (callData) {
      try {
        const terminatePayload = {
          callId,
          msgId,
          action: terminateAction,
        };
        logCallDebug('terminatecall payload', terminatePayload);
        await dispatch(
          terminateCall({
            metadataid: selectedMetadataId,
            phone: callerPhone,
            data: terminatePayload,
          }),
        ).unwrap();
        logCallDebug('terminatecall response', { success: true, action: terminateAction });
      } catch (err) {
        console.error('Error terminating call:', err);
        logCallDebug('terminatecall response', err);
      }
    }
  };

  const handleToggleMute = () => {
    const nextMutedState = toggleMute();
    setIsMuted(nextMutedState);
  };

  const handleToggleHold = () => {
    setIsOnHold((prev) => !prev);
    toggleHoldControl();
  };

  const handleRestore = () => {
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const formatTime = (seconds) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  const formatPhoneNumber = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    if (!digits) return '';

    if (digits.length === 12 && digits.startsWith('91')) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 7)} ${digits.slice(7)}`;
    }

    if (digits.length === 10) {
      return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    }

    if (digits.length > 10) {
      return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10, -5)} ${digits.slice(-5)}`;
    }

    return phone;
  };

  const getInitials = (text) => {
    if (!text) return '';
    const trimmed = String(text).trim();
    if (!trimmed || !Number.isNaN(Number(trimmed))) return '';

    return trimmed
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const customerName =
    callData?.customerName ||
    callData?.name ||
    callData?.whatsappName ||
    callData?.contactName ||
    '';
  const displayPhone = formatPhoneNumber(callerPhone);
  const avatarText = getInitials(customerName);
  const statusTitle = getCallStatusText(callState);
  const footerStatusText = getFooterStatusText(callState);
  const minimizedPhone = displayPhone || callerPhone;
  const minimizedStatus =
    callState === 'connected'
      ? formatTime(callTimer)
      : isIncoming
      ? 'Incoming'
      : callState === 'busy'
      ? 'Busy'
      : callState === 'no-answer'
      ? 'No answer'
      : callState === 'missed'
      ? 'Missed'
      : callState === 'ringing'
      ? 'Ringing'
      : callState === 'initiating' || callState === 'calling'
      ? 'Calling'
      : callState === 'connecting'
      ? 'Connecting'
      : footerStatusText;
  const waveformLabel =
    callState === 'connected'
      ? 'Live audio'
      : isIncoming
      ? 'Incoming call'
      : ['initiating', 'calling', 'ringing', 'connecting'].includes(callState)
      ? 'Calling customer'
      : footerStatusText;

  const onPointerDown = (event) => {
    if (event.button && event.button !== 0) return;
    if (!panelRef.current) return;
    if (event.target?.closest?.('button')) return;

    draggingRef.current = true;
    zIndexRef.current += 1;
    panelRef.current.style.zIndex = zIndexRef.current;

    const rect = panelRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    try {
      headerRef.current?.setPointerCapture?.(event.pointerId);
    } catch (err) {}

    const onPointerMove = (moveEvent) => {
      if (!draggingRef.current || !panelRef.current) return;

      moveEvent.preventDefault();
      const x = moveEvent.clientX - dragOffset.current.x;
      const y = moveEvent.clientY - dragOffset.current.y;
      const rect = panelRef.current.getBoundingClientRect();
      const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 8);

      setPosition({
        left: Math.min(Math.max(8, x), maxLeft),
        top: Math.min(Math.max(8, y), maxTop),
      });
    };

    const onPointerUp = (upEvent) => {
      draggingRef.current = false;
      try {
        headerRef.current?.releasePointerCapture?.(upEvent.pointerId);
      } catch (err) {}

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);

      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      const middle = window.innerWidth / 2;
      setPosition((prev) => ({
        ...prev,
        left: rect.left > middle ? window.innerWidth - rect.width - 28 : 16,
      }));
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  if (!isOpen) return null;

  const stopPanelButtonEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      {!isMinimized && (
        <div
          ref={panelRef}
          className={`call-panel ${callState === 'connected' ? 'visible' : 'entering'}`}
          style={{
            top: position.top ? `${position.top}px` : undefined,
            left: position.left ? `${position.left}px` : undefined,
            right: 'auto',
          }}
        >
          <div
            className="call-header draggable"
            ref={headerRef}
            onPointerDown={onPointerDown}
            onDoubleClick={handleMinimize}
            role="button"
            tabIndex={0}
            aria-label="Drag call panel"
          >
            <div className="call-avatar">
              {avatarText ? (
                <div className="initials">{avatarText}</div>
              ) : (
                <i className="pi pi-user call-avatar-icon" aria-hidden="true"></i>
              )}
            </div>

            <div className="call-meta">
              {customerName ? <div className="caller-title">{customerName}</div> : null}
              <div className="caller-name">{displayPhone || callerPhone}</div>
              <div className="caller-info">{statusTitle}</div>
              {callState === 'connected' && <div className="call-timer">{formatTime(callTimer)}</div>}
              {!error && isTerminalState && endedReason && (
                <div className="caller-info">{endedReason.replace(/-/g, ' ')}</div>
              )}
            </div>

          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="call-body">
            <div className="waveform">
              <div className="waveform-bars" aria-hidden="true">
                {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                  <span
                    key={item}
                    className={`wave-bar ${callState === 'connected' ? 'is-live' : ''}`}
                  ></span>
                ))}
              </div>
              <div className="waveform-label">{waveformLabel}</div>
            </div>

            <div className="actions-wrap">
              <div className="action-card">
                <div className="call-actions-rect">
                  {callState === 'incoming' && (
                    <>
                      <button
                        type="button"
                        className="action-rect primary"
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleAccept();
                        }}
                      >
                        Answer
                      </button>
                      <button
                        type="button"
                        className="action-rect danger"
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleDecline();
                        }}
                      >
                        Decline
                      </button>
                    </>
                  )}

                  {['initiating', 'calling', 'ringing', 'connecting'].includes(callState) && (
                    <>
                      <div className="center">{getCallStatusText(callState)}</div>
                      <button
                        type="button"
                        className="action-rect danger"
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleDisconnect();
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {callState === 'connected' && (
                    <>
                      <button
                        type="button"
                        className={`action-rect secondary ${isMuted ? 'is-active' : ''}`}
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleToggleMute();
                        }}
                      >
                        {isMuted ? 'Unmute' : 'Mute'}
                      </button>
                      <button
                        type="button"
                        className={`action-rect secondary ${isOnHold ? 'is-active' : ''}`}
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleToggleHold();
                        }}
                      >
                        {isOnHold ? 'Resume' : 'Hold'}
                      </button>
                      <button
                        type="button"
                        className="action-rect danger"
                        onPointerDown={stopPanelButtonEvent}
                        onClick={(event) => {
                          stopPanelButtonEvent(event);
                          handleDisconnect();
                        }}
                      >
                        End Call
                      </button>
                    </>
                  )}

                  {isTerminalState && (
                    <button
                      type="button"
                      className="action-rect danger"
                      onPointerDown={stopPanelButtonEvent}
                      onClick={(event) => {
                        stopPanelButtonEvent(event);
                        onClose();
                      }}
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="call-status">
            <span>{footerStatusText}</span>
            {callState === 'connected' && connectionState && connectionState !== 'connected' ? (
              <span className="call-status-tech">{connectionState}</span>
            ) : null}
          </div>
        </div>
      )}

      {isMinimized && (
        <div className="dynamic-island" onClick={handleRestore}>
          <div className="mini-avatar" aria-hidden="true">
            {avatarText ? avatarText : <i className="pi pi-phone"></i>}
          </div>
          <div className="mini-left">
            <div className="caller" title={displayPhone || callerPhone}>
              {minimizedPhone}
            </div>
            <div className="mini-status">{minimizedStatus}</div>
          </div>
          <div className="mini-actions" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="mini-btn danger"
              onPointerDown={stopPanelButtonEvent}
              onClick={(event) => {
                stopPanelButtonEvent(event);
                if (isTerminalState) {
                  onClose();
                  return;
                }
                handleDisconnect();
              }}
            >
              {isTerminalState ? 'Close' : 'End'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CallPanel;
